import { Component, OnInit, OnDestroy, ViewChildren, ElementRef, ViewEncapsulation, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Title } from "@angular/platform-browser";
import { Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { FilterService, MenuItem, MessageService } from 'primeng/api';
import { KeycloakService } from 'keycloak-angular';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Accordion, AccordionTabOpenEvent } from 'primeng/accordion';

import { environment } from 'src/environments/environment';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { GraphData } from 'src/app/shared/dashboard';

/**
 * Application home/dashboard page.
 *
 * Shows six accordion sections — currencies (`c`), payments (`f`), invoices (`i`), products (`p`),
 * Agent ops (`a`), Spam review (`s`) — controlled by URL params `/home/:section/:tab`.  On init, waits for
 * `GlobalSettingsData` via `GetglobalsettingsService`, then `switchMap`s onto
 * `ActivatedRoute.paramMap` so that URL navigation re-routes the active section without
 * triggering a full reload.
 *
 * `departmentHead` is set to `true` when `accessGroup >= 100000`.
 * `canSeeFinanceResult` gates the finance-results section behind Keycloak roles.
 * `canSeeAgentDashboard` gates the Agent ops section behind `portal-admin`.
 */
@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class HomeComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  tabActiveState: boolean[] = [true, true, false, false, false, false];

  production: boolean = environment.production;

  @ViewChild('home') homeAccordion: Accordion

  activePaymentIndex: number = 0;
  ordersActiveIndex: number = 0;
  activeItemIndex: number = 0;

  selectedSection: string = 'c'; // 'c' - currencies, 'f' - finance results, 'i' - accounts, 'p' - products
  selectedTabIndex: number = 0; // depends on selectedSection

  cache: boolean = false;

  /** Expands all six accordion sections. */
  openAllTab() { this.tabActiveState = [true, true, true, true, true, true]; }
  /** Collapses all sections then opens only the section at index `i`. */
  openTab(i: number) { this.tabActiveState = [false, false, false, false, false, false]; this.tabActiveState[i] = true; }

  today: Date = null;

  globalSettinsData: GlobalSettingsData = null;

  clean: boolean = true;

  dayPaymentsToSee: number = 5;
  monthPaymentsToSee: number = 6;
  dayPerformanceToSee: number = 45;

  /** Controls `app-agent-bank-payment-review`'s visibility -- opened from the
   *  "Огляд банківських транзакцій" button in the Платежи tab header, same
   *  audience as `canSeeFinanceResult` (the dialog is server-gated
   *  independently too, `finance-manager` OR `portal-admin`, per
   *  `docs/PROMPT_agent-bank-payment-review-mvp.md`). */
  displayBankPaymentReview: boolean = false;

  /* Roles and access */
  // TODO: replace with a more flexible role/permission system if needed - e.g.,
  // a single method hasPermission('viewFinanceResults') that checks against a permissions list instead of hardcoding role names here
  // Keycloak roles now cached in UserConfigService.userConfig$ -
  // consider injecting UserConfigService and using userConfig.kcRoles.includes('portal-admin')
  // instead of auth.isUserInRole() for better performance and testability

  /** Returns `true` when the user holds the `portal-admin` or `finance-manager` Keycloak role. */
  public get canSeeFinanceResult(): boolean { return (this.auth.isUserInRole('portal-admin') || this.auth.isUserInRole('finance-manager')); }

  /** Returns `true` when the user holds the `portal-admin` Keycloak role -- same audience as
   *  every other Agent Admin surface (`AgentAdmin.ts`, the Team Members/Customer Tier tabs). */
  public get canSeeAgentDashboard(): boolean { return this.auth.isUserInRole('portal-admin'); }

  departmentHead: boolean = false;

  siteUrl: string = 'localhost:4200';

  nameC: string = "Валюта";
  nameF: string = "Результаты";
  nameI: string = "Счета";
  nameP: string = "Товары";

  invTabName1: string = "Новые сегодня";
  invTabName2: string = "Актуальные с резервом";
  invTabName3: string = "Недавно оплаченные";
  invTabName4: string = "Оплаченные, но не отгруженные";
  invTabName5: string = "Просрочки";

  itemTabName1: string = "Оплаченные и не отгруженные";
  itemTabName2: string = "Части сборных изделий: Оплаченные и не отгруженные";
  itemTabName3: string = "Не отгруженные части в разрезе счетов и клиентов";
  itemTabName4: string = "Выставленные";
  itemTabName5: string = "В пути";
  itemTabName6: string = "Поступления на склад";  

  @ViewChild('anchorc') anchorC: ElementRef;
  @ViewChild('anchorf') anchorF: ElementRef;
  @ViewChild('anchori') anchorI: ElementRef;
  @ViewChild('anchorp') anchorP: ElementRef;
  @ViewChild('anchora') anchorA: ElementRef;
  @ViewChild('anchoraspam') anchorSpam: ElementRef;

  tabItems: MenuItem[] = [
    {
      label: 'Все',
      icon: 'pi pi-eye',
      command: () => { this.openAllTab() },
      preserveFragment: true,
      routerLink: '/home',
      replaceUrl: true,
      title: '//1',
    },
    {
      label: 'Валюты',
      icon: 'pi pi-chart-bar',
      command: () => { },
      routerLink: '/home/c/0',
      replaceUrl: true,
      title: '//2',
    },
    {
      label: 'Платежи',
      icon: 'pi pi-money-bill',
      command: () => { },
      routerLink: '/home/f/0',
      replaceUrl: true,
      title: '//3',
      
      items: [
        { label: 'Оплаты за несколько дней', routerLink: '/home/f/0', replaceUrl: true, },
        { label: 'Счета и оплаты за месяц', routerLink: '/home/f/1', replaceUrl: true, },
        { label: 'Тренд помесячно', routerLink: '/home/f/2', replaceUrl: true, },
        { label: 'Лучшие клиенты месяца', routerLink: '/home/f/3', replaceUrl: true, },
        { label: 'Платежи в разрезе клиентов', routerLink: '/home/f/4', replaceUrl: true, },
      ]
    },
    {
      label: 'Счета',
      icon: 'pi  pi-cog',
      command: () => { },
      routerLink: '/home/i/0',
      replaceUrl: true,
      title: '//4',

      items: [
        { label: this.invTabName1, routerLink: '/home/i/0', replaceUrl: true, },
        { label: this.invTabName2, routerLink: '/home/i/1', replaceUrl: true, },
        { label: this.invTabName3, routerLink: '/home/i/2', replaceUrl: true, },
        { label: this.invTabName4, routerLink: '/home/i/3', replaceUrl: true, },
        { label: this.invTabName5, routerLink: '/home/i/4', replaceUrl: true, },
      ]
    },
    {
      label: 'Товары',
      icon: 'pi pi-shopping-cart',
      command: () => { },
      routerLink: '/home/p/0',
      replaceUrl: true,
      title: '//5',

      items: [
        { label: this.itemTabName1, routerLink: '/home/p/0', replaceUrl: true, },
        { label: this.itemTabName2, routerLink: '/home/p/1', replaceUrl: true, },
        { label: this.itemTabName3, routerLink: '/home/p/2', replaceUrl: true, },
        { label: this.itemTabName4, routerLink: '/home/p/3', replaceUrl: true, },
        { label: this.itemTabName5, routerLink: '/home/p/4', replaceUrl: true, },
        { label: this.itemTabName6, routerLink: '/home/p/5', replaceUrl: true, },
      ]
    },
    {
      label: 'Agent',
      icon: 'pi pi-chart-line',
      command: () => { },
      routerLink: '/home/a/0',
      replaceUrl: true,
      title: '//6',
    },
    {
      label: 'Спам',
      icon: 'pi pi-ban',
      command: () => { },
      routerLink: '/home/s/0',
      replaceUrl: true,
      title: '//7',
    },
  ];
  constructor(
    private route: ActivatedRoute,
    private auth: KeycloakService,
    private titleService: Title,
    private filterService: FilterService,
    private globalsettingsservice: GetglobalsettingsService,
    private cdr: ChangeDetectorRef,
  ) {
    const [hash, page, tab, sec] = window.location.hash.split('/');

    this.siteUrl = window.location.protocol + '//' + window.location.hostname + ':' + window.location.port + '/' + hash + '/' + page + '/';
  }

  /** Persists `ordersActiveIndex` to `localStorage` and completes `destroy$`. */
  ngOnDestroy(): void {
    console.log("*** Home in ngOnDestroy");

    localStorage.setItem('HomeComponent.orderPage', JSON.stringify(this.ordersActiveIndex));

    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Opens the accordion section matching `selectedSection` code and sets the active sub-tab to `selectedTabIndex`. */
  routeByParameters(selectedSection: string, selectedTabIndex: number) {
    console.log('*** HomeComponent in routeByParameters: ' + selectedSection + ' ' + selectedTabIndex);
    switch (selectedSection) {
      case 'c':
        this.titleService.setTitle('Обзор: курсы Валют');
        this.openTab(0);
        if (this.anchorC) {
          this.anchorC.nativeElement.scrollIntoView({ behavior: 'smooth' });
        }
        break;

      case 'f':
        this.titleService.setTitle('Обзор: Оплаты и результаты');
        this.openTab(1);
        if (this.anchorF) {
          this.anchorF.nativeElement.scrollIntoView({ behavior: 'smooth' });
        }
        this.activePaymentIndex = selectedTabIndex;
        break;

      case 'i':
        this.titleService.setTitle('Обзор: Выставленные и оплаченные счета');
        this.openTab(2);
        if (this.anchorI) {
          this.anchorI.nativeElement.scrollIntoView({ behavior: 'smooth' });
        }
        this.ordersActiveIndex = selectedTabIndex;
        break;

      case 'p':
        this.titleService.setTitle('Обзор: Проданные и заказанные товары');
        this.openTab(3);
        if (this.anchorP) {
          this.anchorP.nativeElement.scrollIntoView({ behavior: 'smooth' });
        }
        this.activeItemIndex = selectedTabIndex; // 0 - Оплаченные и не отгруженные, 1 - Части сборных изделий: Оплаченные и не отгруженные, 2 - Не отгруженные части в разрезе счетов и клиентов, 3 - Выставленные, 4 - В пути
        break;

      case 'a':
        this.titleService.setTitle('Обзор: Agent — панель стану');
        this.openTab(4);
        if (this.anchorA) {
          this.anchorA.nativeElement.scrollIntoView({ behavior: 'smooth' });
        }
        break;

      case 's':
        this.titleService.setTitle('Обзор: Спам — очередь на проверку');
        this.openTab(5);
        if (this.anchorSpam) {
          this.anchorSpam.nativeElement.scrollIntoView({ behavior: 'smooth' });
        }
        break;

      case 'x':
        this.titleService.setTitle('Обзор - курсы и активность');
        // Do nothing, just stay on the home page
        break;

      default:
        // Do nothing
        this.titleService.setTitle('Обзор - курсы и активность');
        break;
    }
  }

  /** Registers PrimeNG custom filter predicates, loads global settings, then subscribes to URL params to drive section routing. */
  ngOnInit(): void {
    console.log("*** Home in onInit")
    this.titleService.setTitle('Обзор - курсы и активность');
    this.today = new Date();

    this.filterService.register('gtOrZero', (value: number, filter: number) => (
      ((filter == 0) && (value == 0)) || ((filter > 0) && (value > filter))
    ));

    this.filterService.register('GteOrZero', (value: number, filter: number) => (
      ((filter == 0) && (value == 0)) || ((filter > 0) && (value >= filter))
    ));

    // Wait for global settings before setting up dependent subscriptions
    this.globalsettingsservice.getObservableGlobalSettings().pipe(
      map(settings => {
        this.globalSettinsData = settings;
        // Set departmentHead once here after async data arrives, then trigger
        // a new change detection cycle via markForCheck() to avoid
        // ExpressionChangedAfterItHasBeenCheckedError
        this.departmentHead = settings.accessGroup >= 100000;
        this.cdr.markForCheck();
      }),

      switchMap(() => this.route.paramMap),

      takeUntil(this.destroy$),
    )
    .subscribe({
      next: (params: ParamMap) => {
        this.selectedSection = params.get('section') || 'x';
        const tabParam = params.get('tab');
        this.selectedTabIndex = tabParam ? parseInt(tabParam, 10) : 0;

        console.log('*** HomeComponent in param subscribe: (' + this.selectedSection + ') (' + this.selectedTabIndex + ') ' + JSON.stringify(params));

        this.routeByParameters(this.selectedSection, this.selectedTabIndex);
      },
      error: err => console.error('*** HomeComponent subscribe error: ' + JSON.stringify(err))
    });
  }

  /** Handles tab change for the monthly best-clients payment panel (stub — sub-tab logic not yet implemented). */
  handleMonChange(e) {
    var index = e.index;

    console.log('*** Home in handleMonChange: ' + index);

    switch (index) {
      case 0:
        break;

      case 1:
        break;

      case 2:
        break;

      case 3:
        break;

      default:
        // Do nothing
        break;
    }
  }

  /** Resets the `clean` flag to `true`, triggering child components to discard cached filter state. */
  clearSavedSettings() {
    console.log('*** HomeComponent in clearSavedSettings');

    this.clean = true;
  }

  /** Copies `event` text to the system clipboard via the Clipboard API; falls back silently on unsupported browsers. */
  copyClipBoard(event) {
    console.log('*** HomeComponent copyClipBoard');
    if (navigator.clipboard) {
      navigator.clipboard.writeText(event)
        .then(() => console.log('*** HomeComponent copyClipBoard: ' + 'Text copied!'))
        .catch(err => console.error('*** HomeComponent copyClipBoard: ' + 'Failed to copy text:', err));
    } else {
      // Fallback for older browsers (e.g., using document.execCommand('copy'))
      // This method is generally discouraged due to deprecation and limitations.
    }
  }

  /** Handles accordion tab-open events (currently a logging stub). */
  onOpenTab1(event: AccordionTabOpenEvent) {
    console.log('*** HomeComponent in onOpenTab1: ' + JSON.stringify(event));

  }

  /** Updates the browser page title to list the names of all currently open accordion sections.
   *  Fires on every change to the open-tab set -- both a direct accordion-header click and
   *  `openTab()`'s own `tabActiveState` change (routeByParameters()'s navigation path), so
   *  this runs *after* that function's own specific title and can silently overwrite it if a
   *  case is missing here. Case list must cover every index in `tabActiveState` (0-5, one per
   *  accordion section) -- Agent (4)/Спам (5) were missing until 2026-08-05, so opening either
   *  fell through to no title contribution at all. */
  activeIndexChange(event) {
    console.log('*** HomeComponent in activeIndexChange: ' + JSON.stringify(event));
    const opened: number[] = event;

    let title ='';
    opened.forEach(tab => {
      switch (tab) {
        case 0:
          title = title + ' | Курсы валют';
          break;
        case 1:
          title = title + ' | Оплаты и результаты';
          break;
        case 2:
          title = title + ' | Счета';
          break;
        case 3:
          title = title + ' | Товары';
          break;
        case 4:
          title = title + ' | Agent';
          break;
        case 5:
          title = title + ' | Спам';
          break;
      }
    });
    this.titleService.setTitle((title.length > 0) ? 'Обзор' + title + ' |' : 'Обзор - курсы и активность');
  }
}
